import React, { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Label, Stars, RiskBadge, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function Disputes() {
  const { user } = useAuth();
  const isAdmin = user.role !== "employee";
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [decision, setDecision] = useState("UPHELD");
  const [reason, setReason] = useState("");
  const [rejectFeedback, setRejectFeedback] = useState(false);

  const load = () => api.get("/disputes").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const resolve = async () => {
    try {
      await api.patch(`/disputes/${active.id}`, {
        decision, decision_reason: reason,
        feedback_status: (decision === "UPHELD" && rejectFeedback) ? "REJECTED" : undefined,
      });
      toast.success("Dispute resolved"); setActive(null); setReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div><Label>Fairness</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Employee Disputes</h1></div>
      {rows.length === 0 ? <EmptyState icon={ShieldAlert} title="No disputes" hint="Disputed feedback will appear here for review." /> : (
        <div className="space-y-3">
          {rows.map((d) => (
            <div key={d.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`dispute-${d.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-slate-800">{d.employee_name}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">{d.reason}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${d.dispute_status === "PENDING" ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>{d.dispute_status}</span>
                </div>
                {isAdmin && d.dispute_status === "PENDING" && (
                  <Button size="sm" onClick={() => setActive(d)} data-testid={`review-${d.id}`} className="bg-[#4338CA] hover:bg-[#3730A3]">Review</Button>
                )}
              </div>
              {d.detail && <p className="mt-2 text-sm text-slate-600">{d.detail}</p>}
              {d.feedback && (
                <div className="mt-2 flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                  <Stars value={d.feedback.rating} />
                  <RiskBadge status={d.feedback.review_status} />
                  {d.feedback.comment && <span className="text-sm text-slate-500">"{d.feedback.comment}"</span>}
                </div>
              )}
              {d.decision && <p className="mt-2 text-sm text-slate-500">Decision: <b>{d.decision}</b> — {d.decision_reason} <span className="text-slate-400">({d.reviewer})</span></p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Review dispute</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Select value={decision} onValueChange={setDecision}>
              <SelectTrigger data-testid="dispute-decision"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="UPHELD">Uphold dispute (valid concern)</SelectItem>
                <SelectItem value="REJECTED">Reject dispute</SelectItem>
              </SelectContent>
            </Select>
            {decision === "UPHELD" && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={rejectFeedback} onChange={(e) => setRejectFeedback(e.target.checked)} data-testid="dispute-reject-feedback" />
                Exclude this feedback from scores (mark REJECTED)
              </label>
            )}
            <Textarea placeholder="Decision reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dispute-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
            <Button onClick={resolve} disabled={!reason} data-testid="save-dispute" className="bg-[#4338CA] hover:bg-[#3730A3]">Resolve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
